import { z } from 'zod';

/**
 * Brokerage (investment account) statements.
 *
 * A bank statement is a list of transactions that each need an entry. A
 * brokerage statement is different: the account is carried on the books at its
 * total net asset value, and the statement's "Change in NAV" block already
 * explains the month as a handful of components — mark-to-market, dividends,
 * interest, accrual changes, commissions, deposits. One entry per component,
 * dated the statement date, brings the GL to the statement's ending value, and
 * the reconciliation then ties out by construction.
 *
 * Deposits and withdrawals are NOT posted from here: they already reached the
 * books through the bank feed (cash left a checking account and the match
 * posted the other side to the investment account). They are matched, not
 * re-posted.
 */

const ISO_DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
const MONEY = z.string().regex(/^-?\d+(\.\d{1,4})?$/, 'must be a decimal string');

/**
 * What a component of the month's change is. Each kind maps to one GL account
 * in the posting map; `deposits_withdrawals` is informational only.
 */
export const BROKERAGE_COMPONENT_KINDS = [
  'mark_to_market',
  'dividends',
  'credit_interest',
  'bond_coupon',
  'purchase_accrued_interest',
  'margin_interest',
  'other_interest',
  'change_in_interest_accruals',
  'change_in_dividend_accruals',
  'commissions',
  'fees',
  'withholding_tax',
  'other',
  'deposits_withdrawals',
] as const;
export type BrokerageComponentKind = (typeof BROKERAGE_COMPONENT_KINDS)[number];

/** The kinds that produce a journal entry. */
export const BROKERAGE_POSTABLE_KINDS = BROKERAGE_COMPONENT_KINDS.filter(
  (k) => k !== 'deposits_withdrawals',
) as Exclude<BrokerageComponentKind, 'deposits_withdrawals'>[];

export const BROKERAGE_KIND_LABELS: Record<BrokerageComponentKind, string> = {
  mark_to_market: 'Mark-to-market (unrealized gain or loss)',
  dividends: 'Dividends',
  credit_interest: 'Credit interest on cash',
  bond_coupon: 'Bond interest received',
  purchase_accrued_interest: 'Accrued interest paid on purchases',
  margin_interest: 'Margin / debit interest charged',
  other_interest: 'Interest (unclassified)',
  change_in_interest_accruals: 'Change in interest accruals',
  change_in_dividend_accruals: 'Change in dividend accruals',
  commissions: 'Commissions',
  fees: 'Fees',
  withholding_tax: 'Withholding tax',
  other: 'Other',
  deposits_withdrawals: 'Deposits & withdrawals',
};

/** One detail row behind a component (an interest line, a dividend, a deposit). */
export const brokerageDetailLineSchema = z.object({
  date: ISO_DATE.nullable(),
  description: z.string(),
  /** Signed: positive raises the account's value. */
  amount: MONEY,
});
export type BrokerageDetailLine = z.infer<typeof brokerageDetailLineSchema>;

export const brokerageComponentSchema = z.object({
  kind: z.enum(BROKERAGE_COMPONENT_KINDS),
  /** The line as the statement printed it ("Mark-to-Market", "Change in Interest Accruals"). */
  label: z.string(),
  /** Signed effect on the account's value. */
  amount: MONEY,
  lines: z.array(brokerageDetailLineSchema).default([]),
});
export type BrokerageComponent = z.infer<typeof brokerageComponentSchema>;

export const brokerageHeaderSchema = z.object({
  statementDate: ISO_DATE.nullable(),
  periodFrom: ISO_DATE.nullable(),
  periodTo: ISO_DATE.nullable(),
  /** Last 4 digits of the account number (U21505345 → "5345"). */
  accountMask: z.string().nullable(),
  institutionName: z.string().nullable(),
  startingValue: MONEY.nullable(),
  endingValue: MONEY.nullable(),
  confidence: z.number().min(0).max(1).default(0),
});
export type BrokerageHeader = z.infer<typeof brokerageHeaderSchema>;

export const parsedBrokerageStatementSchema = z.object({
  header: brokerageHeaderSchema,
  components: z.array(brokerageComponentSchema),
  /**
   * starting + Σ components − ending, to the cent. Zero when the statement
   * reads cleanly; anything else is shown to the owner, never papered over.
   */
  unexplained: MONEY,
  warning: z.string().nullable(),
});
export type ParsedBrokerageStatement = z.infer<typeof parsedBrokerageStatementSchema>;

// ── Posting map ──────────────────────────────────────────────────────────────

/** Component kind → GL account id. Stored per investment account, remembered from the last post. */
export const brokeragePostingMapSchema = z.record(z.string(), z.string().uuid().nullable());
export type BrokeragePostingMap = z.infer<typeof brokeragePostingMapSchema>;

export const brokerageProposalInputSchema = z.object({
  glAccountId: z.string().uuid(),
  brokerage: parsedBrokerageStatementSchema,
});
export type BrokerageProposalInput = z.infer<typeof brokerageProposalInputSchema>;

/** One proposed entry: the component, the account it offsets to, and the memo. */
export const brokerageProposedLineSchema = z.object({
  kind: z.enum(BROKERAGE_COMPONENT_KINDS),
  label: z.string(),
  amount: MONEY,
  accountId: z.string().uuid().nullable(),
  /** Where the account came from: the saved map, a name-based guess, or nothing. */
  accountSource: z.enum(['map', 'suggested', 'none']),
  memo: z.string(),
  lines: z.array(brokerageDetailLineSchema).default([]),
});
export type BrokerageProposedLine = z.infer<typeof brokerageProposedLineSchema>;

export const brokerageProposalSchema = z.object({
  glAccountId: z.string().uuid(),
  statementDate: ISO_DATE.nullable(),
  lines: z.array(brokerageProposedLineSchema),
  /** Entries already posted from a statement of this date on this account, if any. */
  alreadyPosted: z.array(
    z.object({ journalEntryId: z.string().uuid(), entryNumber: z.string(), memo: z.string().nullable() }),
  ),
});
export type BrokerageProposal = z.infer<typeof brokerageProposalSchema>;

export const brokeragePostLineSchema = z.object({
  kind: z.enum(BROKERAGE_COMPONENT_KINDS),
  label: z.string().max(200),
  amount: MONEY,
  accountId: z.string().uuid(),
  memo: z.string().max(500),
});

export const brokeragePostInputSchema = z.object({
  glAccountId: z.string().uuid(),
  statementDate: ISO_DATE,
  institutionName: z.string().max(120).nullable().optional(),
  lines: z.array(brokeragePostLineSchema).min(1).max(30),
  /** Remember each kind's account on the investment account for next month. Default true. */
  saveMap: z.boolean().default(true),
});
export type BrokeragePostInput = z.infer<typeof brokeragePostInputSchema>;

export const brokeragePostResultSchema = z.object({
  entries: z.array(
    z.object({
      journalEntryId: z.string().uuid(),
      entryNumber: z.string(),
      kind: z.enum(BROKERAGE_COMPONENT_KINDS),
      amount: MONEY,
    }),
  ),
});
export type BrokeragePostResult = z.infer<typeof brokeragePostResultSchema>;
